/**
 * @file plugins/childDemographics.js
 * @author Jake Stark
 * @desc Child Demographics report plugin

*/

/**
 * @module plugins/childDemographics
 * @desc Child Demographics report plugin
 */

define('plugins/childDemographics',["text!pluginTemplates/childDemographics_ethnicity_table.html!strip", "text!pluginTemplates/childDemographics_participationByRace_table.html!strip", "text!pluginTemplates/childDemographics_eecByRace_table.html!strip",
    "models", "cardboard/models", "cardboard/collections", "cardboard/pluginPieces"
],
    function (ethnicityTableTemplate, participationTableTemplate, eecTableTemplate,
        ECLDSModels, CardboardModels, CardboardCollections, PluginPieces) {

        console.log("MODULE: childDemographics");

        var exports = {

            // other names by which this plugin is known. this exists for
            // WebFOCUS shim compatibility
            //aliases: ["demographic", "demographics"],
            options: {
                //noDefaultOrg: true,
                orgDetailPane: false,
                noMapSearch: true,
                noOrgFinderSchool: true,
                orgFinderDistrict: true,
                forcedSummaryFilters: ["FISCAL_YEAR", "RACE_ETHTYPE"],
                definitionsECLDS: true
            },

            // the options available to these filters will be populated
            // from the database, via the below data sources
            filters: {
                // no filters!
            },

            // each of these is the configuration that gets passed to a
            // DataBoxPaneView.
            panes: [{
                title: "Kindergartner's Race/Ethnicity ",
                explanationText: "<p>This chart shows the race/ ethnicity and other criteria of public school kindergartners as identified in their education record.</p><p>The Minnesota Department of Education (MDE) now reports students using eight state-defined racial and ethnic groups, including the use of the state definition of American Indian and the new Other Indigenous Peoples group. Students count as American Indian if they meet the state definition regardless of any other racial or ethnic groups with which they may have identified.</p>",
                openByDefault: true,

                views: [{
                    type: "template",
                    template: "<h5>{{data.heading}}</h5> ",
                    dataSourceName: "data",
                    dataPath: "dataSets.raceAndEthnicity"
                },
                {
                    type: "template",
                    template: "<button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                },
                {
                    // SETUP
                    type: "bar",
                    dataSourceName: "data",
                    dataSetPath: "dataSets.raceAndEthnicity",
                    //dataPath: "dataSets.raceAndEthnicity.data", 
                    // all paths in the
                    // chartable series are
                    // relative to this blob

                    // "pointsWithinRows": given that the data is
                    // two-dimensional (meaning an array of arrays, or
                    // similar structure, implying that we have more than
                    // one series to plot) each series is contained in a
                    // "row", like so:
                    //	 +---------+
                    //	 | X X X X |
                    //	 | . . . . |
                    //	 | . . . . |
                    //	 | . . . . |
                    //	 +---------+
                    dataShape: "pointsWithinRows",

                    // since in this case there's only one dimension, due to
                    // there being only one series to plot, it looks like
                    // this:
                    //	 +---------+
                    //	 | X X X X |
                    //	 +---------+
                    convertDataHashToArray: true,
                    dataDimensions: 1,
                    title: "",
                    exportToPDF: true,
                    hideContext: true,

                    // DISPLAY
                    unitString: "%",
                    unitDec: 1,

                    showLegend: false,
                    dataLabels: false,
                    disableLegendClick: false,

                    // SERIES (only one series, this is a simple graph)
                    series: [{
                        valuePath: "percent",
                        displayNamePath: "raceEthnicityName",
                        longDisplayNamePath: "raceEthnicityName",
                        //     colors: ["blue1", "green", "blue2", "grey2", "purple"],
                         colorPath: "color"
                    }],
                    errorConditions: [PluginPieces.standardErrorCondition]
                },

                {
                    type: "template",
                    dataSourceName: "data",
                    dataSetPath: "dataSets.raceAndEthnicity",
                    template: ethnicityTableTemplate,
                    errorConditions: [PluginPieces.silentStandardErrorCondition]
                }]
            }, {
                title: "Program Participation by Race/Ethnicity",
                explanationText: "<p>This chart shows public school kindergartners by race and ethnicity who participated in at least one of the following public early care and education programs: Child Care Assistance (CCAP), Early Childhood Family Education (ECFE), Early Childhood Special Education (ECSE) or School District Preschool (MN District Preschool) prior to kindergarten entry.</p><p>Not all children participating in these programs may have attended public kindergarten in Minnesota. Many children attend private child care and early learning programs prior to kindergarten and are not included in these counts. </p> <p>More information about the <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=MDE034819&RevisionSelectionMethod=latestReleased&Rendition=primary'  target='_blank'>public programs included in ECLDS</a> is available.</p>The Minnesota Department of Education (MDE) now reports students using eight state-defined racial and ethnic groups, including the use of the state definition of American Indian and the new Other Indigenous Peoples group. Students count as American Indian if they meet the state definition regardless of any other racial or ethnic groups with which they may have identified.</p>",
                openByDefault: false,

                views: [{
                    type: "template",
                    template: "<h5>{{data.heading}}</h5>",
                    dataSourceName: "data",
                    dataPath: "dataSets.participationRaceEthnicities"
                }, {
                    type: "template",
                    template: "<p>NOTE: Because ECFE serves children from birth to Kindergarten entrance, and ECFE data begins with the 2013-14 school year, ECLDS most fully represents ECFE participation in Kindergarten reports beginning with the 2019-20 school year.</p><button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                }, {
                    // SETUP
                    type: "stackedBar",
                    dataSourceName: "data",
                    dataSetPath: "dataSets.participationRaceEthnicities",

                    // "pointsAcrossRows": given that the data is
                    // two-dimensional (meaning an array of arrays, or
                    // similar structure, implying that we have more than
                    // one series to plot) each series is contained in a
                    // "column", like so:
                    //	 +---------+
                    //	 | X . . . |
                    //	 | X . . . |
                    //	 | X . . . |
                    //	 | X . . . |
                    //	 +---------+
                    // To wrap your head around this, watch iteration in your debugger in
                    // pointsAcrossRows() in transforms.js.
                    dataShape: "pointsAcrossRows",
                    convertDataHashToArray: true,
                    dataDimensions: 2,
                    unitDec: 1,
                    title: "",

                    // DISPLAY
                    xAxisLabelPath: "raceEthnicityName",
                    legendReversed: false,
                    unitString: "%",
                    exportToPDF: true,
                    hideContext: true,
                    stackLabels: false,
                    reversedStacks: false,
                    showLegend: true,
                    disableLegendClick: false,

                    // SERIES (only one series, this is a simple graph)
                    series: [{
                        valuePath: "participation[0].percent",
                        seriesDisplayName: "Known public ECE participation",
                        //    color: "blue1"
                       
                    }, {
                        valuePath: "participation[1].percent",
                        seriesDisplayName: "No ECE data available",
                        //     color: "green"
                        
                    }],
                    errorConditions: [PluginPieces.standardErrorCondition]
                },

                {
                    type: "template",
                    dataSourceName: "data",
                    dataSetPath: "dataSets.participationRaceEthnicities",
                    template: participationTableTemplate,
                    errorConditions: [PluginPieces.silentStandardErrorCondition]
                }]
            }, {
                title: "Participation by Race/Ethnicity and Program ",
                explanationText: "<p>This chart shows distribution by race and ethnicity of public school kindergartners who participated in some public early care and education programs prior to kindergarten entry. Some children participated in more than one program. </p><p>Not all children participating in these programs may have attended public kindergarten in Minnesota. Many children attend private child care and early learning programs prior to kindergarten and are not included in these counts. </p> <p>More information about the <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=MDE034819&RevisionSelectionMethod=latestReleased&Rendition=primary'  target='_blank'>public programs included in ECLDS</a> is available. </p><p>The Minnesota Department of Education (MDE) now reports students using eight state-defined racial and ethnic groups, including the use of the state definition of American Indian and the new Other Indigenous Peoples group. Students count as American Indian if they meet the state definition regardless of any other racial or ethnic groups with which they may have identified. </p>",       
                openByDefault: false,

                views: [{
                    type: "template",
                    template: "<h5>{{data.heading}}</h5>",
                    dataSourceName: "data",
                    dataPath: "dataSets.eecRaceEthnicities"
                }, {
                    type: "template",
                    template: "<p>NOTE: Some children may have participated in more than one program.</p> <p>NOTE: Because ECFE serves children from birth to Kindergarten entrance, and ECFE data begins with the 2013-14 school year, ECLDS most fully represents ECFE participation in Kindergarten reports beginning with the 2019-20 school year.</p><button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                },

                {
                    // SETUP
                    type: "stackedBar",
                    dataSourceName: "data",
                    dataSetPath: "dataSets.eecRaceEthnicities",

                    // "pointsAcrossRows": given that the data is
                    // two-dimensional (meaning an array of arrays, or
                    // similar structure, implying that we have more than
                    // one series to plot) each series is contained in a
                    // "column", like so:
                    //	 +---------+
                    //	 | X . . . |
                    //	 | X . . . |
                    //	 | X . . . |
                    //	 | X . . . |
                    //	 +---------+
                    // To wrap your head around this, watch iteration in your debugger in
                    // pointsAcrossRows() in transforms.js.
                    dataShape: "pointsWithinRows",
                    convertDataHashToArray: true,
                    dataDimensions: 2,
                    disableLegendClick: false,

                    // DISPLAY
                    //xAxisLabelPath: "programName",
                    //yLabel: "Percentage of Race/ Ethnicity",
                    legendReversed: false,
                    unitString: "%",
                    title: "",
                    unitDec: 1,
                    dataLabels: false,
                    reversedStacks: false,
                    exportToPDF: true,
                    hideContext: true,
                    showLegend: true,
                    stackLabels: false,
                    hoverSeriesName: "Y",

                    series: [
                        {
                            dataPath: "programs",
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                            displayNamePath: "programName",
                            longDisplayNamePath: "raceEthnicityName",
                            // colors: ["blue1", "green", "blue2","grey2",  "purple", "green2",  "gold", "orange"],
                            colorPath: "color"
                        }


                    ],
                    /*     series: [{
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        }, {
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        },{
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        }, {
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        },{
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        }, {
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        },{
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        }, {
                            valuePath: "percent",
                            seriesDisplayNamePath: "raceEthnicityName",
                           // seriesDisplayName: "Known public ECE participation",
                            //    color: "blue1"
                            color: "color"
                        }], */

                    errorConditions: [PluginPieces.standardErrorCondition]
                },
                {
                    type: "template",
                    dataSourceName: "data",
                    dataSetPath: "dataSets.eecRaceEthnicities",
                    template: eecTableTemplate,
                    errorConditions: [PluginPieces.silentStandardErrorCondition]
                }
                ]
            }, {
                title: "For More Information",
                visibleByDefault: true,
                openByDefault: false,
                views: [{
                    type: "template",
                    template: "<p>Children attending private child care and early learning programs prior to kindergarten are not included in these counts. Not all children participating in these programs may have attended public kindergarten in Minnesota. </p><p>Program Participation by Race/Ethnicity:  This chart shows public school kindergartners by race/ethnicity who participated in at least one of the following public early care and education programs: Child Care Assistance (CCAP), Early Childhood Family Education (ECFE), Early Childhood Special Education (ECSE) or School District Preschool (MN District Preschool) prior to kindergarten entry.</p><p> As of June 2021, Voluntary Prekindergarten (VPK) and School Readiness Plus (SRP) data are included in the MN District Preschool data provided by MDE. School Readiness data begin with the 2013-2014 school year; VPK data begin with the 2016-2017 school year; SRP data begin with the 2017-2018 school year.</p><h2> Glossary</h2><p><strong> CTSTR</strong>: Counts Too Small To Report - when results are less than 10.<br/><strong> ECE</strong>: Early Care and Education.<br/><strong>Kindergarten Cohort</strong>: A group of children who participated in public kindergarten in a given school year.<br/><strong> No Data Available</strong>: No data is available for the selected criteria.<br/><strong> Race/Ethnicity:</strong><ul style='margin-top: -7px; margin-bottom: -15px;'><li>Federal Race/Ethnicity categories are as reported by the child's education record and align with the federal <a href='https://www.govinfo.gov/content/pkg/FR-1997-10-30/pdf/97-28653.pdf' target='_blank'>Office of Management and Budget’s standards</a>.</li><li>State Race/Ethnicity categories are as follows:<ul style='padding-left: 1em;'><li  style='list-style: circle'><strong>State (Minnesota) Definition of American Indian</strong>: A person having origins in any of the original peoples of North America and who maintains cultural identification through tribal affiliation or community recognition. Any student or descendant from a North American tribe, regardless of other racial backgrounds.</li><li  style='list-style: circle'><strong>State (Minnesota) Definition of Other Indigenous Peoples</strong>: A student whose only racial or ethnic identification is with the original peoples of Central or South America. A student who meets the federal definition of American Indian or Alaska Native but not the state definition of American Indian, which means they identify only with indigenous peoples from Central and South America.</li><li style='list-style: circle'>State (Minnesota) definitions of race/ethnicity other than American Indian and Other Indigenous Peoples align with federal categories</li></ul></li></ul></p><h2>Selection Criteria Definitions</h2><p><strong> School County</strong>: County of the location of public school of enrollment. <br/><strong>School District</strong>: School district in the state of Minnesota.<br/><strong> School Year</strong>: Period of time that covers July 1 of one year through June 30 of the following.<br/><b>Disability Type</b>: Disability type as identified within Minnesota's K-12 public school system. For kindergarten cohort analyses disability type originates in MDE data.<br/><b> Home Language</b>: A kindergarten cohort utilizes MDE home language types as defined in the MDE K12 data set. ECLDS provides the home language types containing 10 or more children. <br/><b>English Learner</b>: (ELL) Stands for English Language Learner  A classification for a student whose home language is other than English and who has been assessed as needing additional English language instruction.<br/><b> Free/Reduced Price Meals</b>: Food program offered through public schools for low-income children. Family income must be at 100% of Federal Poverty Guidelines or lower for Free Lunch and between 101% and 185% Federal Poverty Guidelines for Reduced Price Meals. <br/><b>The Minnesota Family Investment Program (MFIP) and Diversionary Work Program (DWP):</b> Provide income assistance to help families with low income move to financial stability through work. MFIP is Minnesota's Temporary Assistance to Needy Families (TANF) program. DWP is a four-month program that helps parents quickly find work so that they do not need to go on MFIP. When selected looks at a child's receipt of MFIP or DWP any time prior during their kindergarten year.<br/><b> SNAP</b>: Supplemental Nutrition Assistance Program, formerly known as Food Stamps, helps Minnesotans with low income get the food they need for nutritious and well-balanced meals. When selected looks at child's receipt of SNAP any time prior to and including their kindergarten year.<br/><b> Preterm Birth</b>: An infant who is born before 37 weeks gestational age as calculated from the imputed gestational age based on last menstrual period. Data is obtained from the child's birth record. <br/><b> Low Birth Weight</b>: An infant that is born weighing less than 2,500 g. Data is obtained from the child's birth record. </p><p> For more information, open the <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde034818&RevisionSelectionMethod=latestReleased&Rendition=primary'  target='_blank'>ECLDS Data Guide. </a></p>"
                }]
            }],

            filterSources: {
                orgs: {
                    modelType: ECLDSModels.ECLDSOrgList,
                    //url: "/data/state.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_orgid_childdemographics.fex",
                    noViewParams: true,
                    enabled: true
                },
                filters: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_childdemographics_EcoDevRegion.fex"
                        //filterForReport: "data/childDemographics3.json"
                    },
                    enabled: true,
                    noViewParams: false
                },

                filters2: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_fiscal_year.fex",
                    enabled: true,
                    modelType: CardboardModels.FilterData
                },

                filters3: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    //    defaultParams: ["FISCAL_YEAR=2014"],
                    urls: {
                        //filterForReport: "data/filtersChildDemo.json"
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_childDemographics.fex"
                    },

                    // specify the order of filters, by name (filterUrl in database terms)
                    filterOrder: ["GENDER", "DISABILITY_TYPE", "HOME_LANGUAGE", "OTHER_CRITERIA"],

                    enabled: true,
                    noViewParams: false
                },

                demographicCategories: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_childdemographics_nbr2",
                    enabled: true,
                    modelType: CardboardCollections.DemoCatProducer,
                    noViewParams: false
                }
            },

            dataSources: {
                data: {
                    modelType: CardboardModels.Model,
                    //url: "/data/childDemographics.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_childDemographics",
                    yearPath: "meta.fiscalYear", // where to look in the JSON for the year
                    //	defaultParams: {
                    //		orgId: 999999000,
                    //      COHORTID: 2014
                    //	},
                    enabled: true,
                    errorConditions: [PluginPieces.standardErrorCondition]
                }
            }
        };

        return exports;
    });
